import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import LessonsPage from "./pages/LessonsPage";
import StudentsPage from "./pages/StudentsPage";
import AttendancePage from "./pages/AttendancePage";
import GradingPage from "./pages/GradingPage";
import CriteriaPage from "./pages/CriteriaPage";
import UsersPage from "./pages/UsersPage";
import ReportsPage from "./pages/ReportsPage";
import ArchivePage from "./pages/ArchivePage";
import DeletionManagementPage from "./pages/DeletionManagementPage";
import SettingsPage from "./pages/SettingsPage";
import AssessmentFormsPage from "./pages/AssessmentFormsPage";
import FormBuilderPage from "./pages/FormBuilderPage";
import AssessmentsPage from "./pages/AssessmentsPage";
import LessonDetailPage from "./pages/LessonDetailPage";
import AssessmentDetailPage from "./pages/AssessmentDetailPage";
import GroupsPage from "./pages/GroupsPage";
import SkillFormsPage from "./pages/SkillFormsPage";
import SkillAssessmentsPage from "./pages/SkillAssessmentsPage";
import SkillAssessmentDetailPage from "./pages/SkillAssessmentDetailPage";
import POEPage from "./pages/POEPage";
import StudentPOEPage from "./pages/StudentPOEPage";
import POEUnitDetailPage from "./pages/POEUnitDetailPage";
import POESetupPage from "./pages/POESetupPage";
import "./index.css";

// Error Boundary
class ErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false }; }
    static getDerivedStateFromError() { return { hasError: true }; }
    componentDidCatch(error, errorInfo) { console.error('App Error:', error, errorInfo); }
    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-background flex items-center justify-center p-4">
                    <div className="text-center max-w-md">
                        <h2 className="text-xl font-bold text-foreground mb-2">Something went wrong</h2>
                        <p className="text-muted-foreground mb-4">An unexpected error occurred. Please refresh the page.</p>
                        <button onClick={() => { this.setState({ hasError: false }); window.location.href = '/dashboard'; }}
                            className="px-4 py-2 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#152a47] transition-colors" data-testid="error-boundary-btn">
                            Go to Dashboard
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f] dark:border-ica-gold"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// Admin-only Route wrapper
const AdminRoute = ({ children }) => {
    const { user, loading, isAdmin } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f] dark:border-ica-gold"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (!isAdmin()) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Public Route - redirects to dashboard if already logged in
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f] dark:border-ica-gold"></div>
            </div>
        );
    }

    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public routes */}
            <Route 
                path="/login" 
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                } 
            />

            {/* Protected routes */}
            <Route 
                path="/dashboard" 
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/lessons" 
                element={
                    <ProtectedRoute>
                        <LessonsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/lessons/:id" 
                element={
                    <ProtectedRoute>
                        <LessonDetailPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/students" 
                element={
                    <ProtectedRoute>
                        <StudentsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/attendance" 
                element={
                    <ProtectedRoute>
                        <AttendancePage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/grading" 
                element={
                    <ProtectedRoute>
                        <GradingPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/criteria" 
                element={
                    <ProtectedRoute>
                        <CriteriaPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/users" 
                element={
                    <AdminRoute>
                        <UsersPage />
                    </AdminRoute>
                } 
            />
            <Route 
                path="/reports" 
                element={
                    <ProtectedRoute>
                        <ReportsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/archive" 
                element={
                    <ProtectedRoute>
                        <ArchivePage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/deletion" 
                element={
                    <AdminRoute>
                        <DeletionManagementPage />
                    </AdminRoute>
                } 
            />
            <Route 
                path="/settings" 
                element={
                    <ProtectedRoute>
                        <SettingsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/assessments" 
                element={
                    <ProtectedRoute>
                        <AssessmentsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/assessments/:id" 
                element={
                    <ProtectedRoute>
                        <AssessmentDetailPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/assessment-forms" 
                element={
                    <ProtectedRoute>
                        <AssessmentFormsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/assessment-forms/:id" 
                element={
                    <ProtectedRoute>
                        <FormBuilderPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/groups" 
                element={
                    <ProtectedRoute>
                        <GroupsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/skill-forms" 
                element={
                    <ProtectedRoute>
                        <SkillFormsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/skill-assessments" 
                element={
                    <ProtectedRoute>
                        <SkillAssessmentsPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/skill-assessments/:id" 
                element={
                    <ProtectedRoute>
                        <SkillAssessmentDetailPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/poe" 
                element={
                    <ProtectedRoute>
                        <POEPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/poe/student/:studentId" 
                element={
                    <ProtectedRoute>
                        <StudentPOEPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/poe/unit/:unitId" 
                element={
                    <ProtectedRoute>
                        <POEUnitDetailPage />
                    </ProtectedRoute>
                } 
            />
            <Route 
                path="/admin/poe-setup" 
                element={
                    <AdminRoute>
                        <POESetupPage />
                    </AdminRoute>
                } 
            />

            {/* Redirect root to dashboard or login */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <ErrorBoundary>
            <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster 
                        position="top-right"
                        toastOptions={{
                        className: 'shadow-lg',
                    }}
                />
            </BrowserRouter>
        </AuthProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
